/* eslint-disable camelcase */
import { Spinner } from '@mm/components/src/Spinner/Spinner';
import { CheckCircleIcon } from '@mm/icons/general/CheckCircleIcon';
import { CloseCircleSolidIcon } from '@mm/icons/general/CloseCircleSolidIcon';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { UniversalUpgradeModalProps } from '@mm/state/modules/modalsState/props';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  combinedFeatureFlagsSelector,
  getPlanFromBillingType,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import {
  defaultProPlanSelector,
  defaultProUnicornPlanSelector,
  defaultTeamPlanSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useMachine, useSelector as useXstateSelector } from '@xstate/react';
import teamImage from 'assets/plans/agencio.png';
import unicornImage from 'assets/plans/chatfunnel.png';
import proImage from 'assets/plans/smbots.png';
import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { colors } from '../../../editor/features/beta/styles/colors';
import { SettingsRoutes } from '../../../routes/settings-routes';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import BasicModalTemplate from '../BasicModalTemplate';
import { ModalInstanceProps } from '../interfaces';
import './UniversalUpgradeModal.pcss';
import {
  NewPlans,
  UpgradeLandmineEvents,
  UpgradeLandmineStates,
  getUpgradeLandmineMachine,
} from './upgradeLandmineMachine';
import { useLandmineOpensHelpers } from './useLandmineOpensHelpers';

export const UniversalUpgradeModal = (
  props: UniversalUpgradeModalProps & ModalInstanceProps,
) => {
  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);
  const upgradeLandmineMachine = useMemo(
    () => getUpgradeLandmineMachine(november2021Pricing),
    [november2021Pricing],
  );

  return (
    <UniversalUpgradeModalBase
      {...props}
      upgradeLandmineMachine={upgradeLandmineMachine}
      key={upgradeLandmineMachine.id}
    />
  );
};

export const UniversalUpgradeModalBase = ({
  landmineName,
  feature,
  planFallback,
  stayOnPage,
  onSuccessBilling,
  closeModal,
  upgradeLandmineMachine,
  otherFacebookPage,
}: UniversalUpgradeModalProps &
  ModalInstanceProps & {
    upgradeLandmineMachine: ReturnType<typeof getUpgradeLandmineMachine>;
  }) => {
  const history = useHistory();
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const facebookPage = otherFacebookPage
    ? otherFacebookPage
    : activeFacebookPage;
  const isEditable = useSelector(activeCompanySelector)?.editable;
  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);

  const nextBillingTypeFromFeature = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, feature),
  );
  const activeCompany = useSelector(activeCompanySelector);

  const { november2021Pricing, billing2023 } = useSelector(
    combinedFeatureFlagsSelector,
  );

  const adjustedPlanFallback = november2021Pricing
    ? planFallback === PageBillingType.Pro
      ? PageBillingType.ProUnicorn
      : planFallback
    : planFallback;

  const nextBillingType = adjustedPlanFallback || nextBillingTypeFromFeature;
  const skipFetchActivePage = otherFacebookPage
    ? otherFacebookPage?.id !== activeFacebookPage?.id
    : false;

  const { landmineOpenId, landmineOpensCRUDActions } = useLandmineOpensHelpers(
    landmineName,
    getPlanFromBillingType(nextBillingType),
    !!billing2023,
  );

  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const viewActions = useViewActions();
  const modalActions = useModalActions();

  useEffect(() => {
    if (!currentSubscription && facebookPage) {
      subscriptionCRUDActions.getCurrentSubscription(
        facebookPage?.id,
        false,
        facebookPage.subscription?.id,
        isNewBillingSubscription,
      );
    }
  }, [currentSubscription]);

  const defaultProPlan = useSelector(defaultProPlanSelector);
  const defaultUnicornPlan = useSelector(defaultProUnicornPlanSelector);
  const defaultTeamPlan = useSelector(defaultTeamPlanSelector);

  const [landmineMachineState, send, service] = useMachine(
    upgradeLandmineMachine,
    {
      devTools: false,
      context: {
        persona: activeCompany?.persona,
        nextBillingType,
      },
    },
  );

  const getPlanPrices = (plan: EnhancedPlan) => ({
    monthlyPrice: plan.unitAmountInCents ? plan.unitAmountInCents / 100 : 0,
    monthlyPricePaidYearly: plan.annualUnitAmountInCents
      ? plan.annualUnitAmountInCents / 100
      : 0,
  });

  useEffect(() => {
    if (defaultProPlan && defaultUnicornPlan && defaultTeamPlan) {
      send({
        type: UpgradeLandmineEvents.setPlanPrices,
        planPrices: {
          [UpgradeLandmineStates.Operator]: getPlanPrices(defaultProPlan),
          [UpgradeLandmineStates.Startup]: getPlanPrices(defaultUnicornPlan),
          [UpgradeLandmineStates.Agency]: getPlanPrices(defaultTeamPlan),
        },
      });
    }
  }, [defaultProPlan, defaultUnicornPlan, defaultTeamPlan]);

  const selectedPlan = useXstateSelector(
    service,
    state => state.value,
  ) as UpgradeLandmineStates;

  const plans = useXstateSelector(
    service,
    state => state.context.availablePlans,
  );
  const planCopy = useXstateSelector(service, state => state.context.planCopy);
  const planPrices = useXstateSelector(
    service,
    state => state.context.planPrices?.[state.value as NewPlans],
  );

  const getClassNames = (baseClassName: string) =>
    classNames(baseClassName, {
      [`${baseClassName}--pro`]:
        selectedPlan === UpgradeLandmineStates.Operator,
      [`${baseClassName}--unicorn`]:
        selectedPlan === UpgradeLandmineStates.Startup,
      [`${baseClassName}--team`]: selectedPlan === UpgradeLandmineStates.Agency,
    });

  // Note: checks other than state being `init` are not really needed.
  // They are here not to do extra checks in multiple places in the code below.
  if (
    landmineMachineState.value === UpgradeLandmineStates.init ||
    !planCopy ||
    !planPrices ||
    !selectedPlan
  ) {
    return <Spinner />;
  }

  const planImage =
    selectedPlan === UpgradeLandmineStates.Operator
      ? proImage
      : selectedPlan === UpgradeLandmineStates.Startup
      ? unicornImage
      : teamImage;

  const onUpgrade = () => {
    if (landmineOpenId) {
      landmineOpensCRUDActions.update(landmineOpenId, true);
    }

    const billingPlan =
      selectedPlan === UpgradeLandmineStates.Operator
        ? defaultProPlan
        : selectedPlan === UpgradeLandmineStates.Startup
        ? defaultUnicornPlan
        : defaultTeamPlan;

    if (billingPlan) {
      modalActions.openBillingModal({
        facebookPage,
        plan: billingPlan,
        landmineOpenId,
        stayOnPage,
        onSuccessBilling,
        subscription: facebookPage?.subscription,
        upgradetoTeam: selectedPlan === UpgradeLandmineStates.Agency,
        skipFetchActivePage,
      });
    } else {
      viewActions.setLandmineOpenId(landmineOpenId);
      history.push(SettingsRoutes.billing.getLink());
      closeModal();
    }
  };

  const modalTopClassName = november2021Pricing
    ? 'universal-upgrade-landmine-modal__top-narrow'
    : 'universal-upgrade-landmine-modal__top';

  const modalClassName = classNames('universal-upgrade-landmine-modal', {
    narrow: november2021Pricing,
  });

  modalActions.openNewBillingModal2023({ landmineName, onSuccessBilling });

  return (
    <BasicModalTemplate
      topClassName={modalTopClassName}
      className={modalClassName}
    >
      <div className={getClassNames('universal-upgrade-landmine-modal__left')}>
        <div className="universal-upgrade-landmine-modal__menu">
          {plans?.map((p, i) => (
            <div
              key={i}
              className={classNames(
                'universal-upgrade-landmine-modal__menu-item',
                {
                  'universal-upgrade-landmine-modal__menu-item--selected':
                    p === selectedPlan,
                },
              )}
              onClick={() =>
                send({ type: UpgradeLandmineEvents.switchPlan, plan: p })
              }
            >
              {p}
            </div>
          ))}
        </div>
        <div className="universal-upgrade-landmine-modal__left-content">
          <div className="universal-upgrade-landmine-modal__left-content--image">
            <img src={planImage} alt="Plan badge" />
          </div>

          <div
            className={getClassNames(
              'universal-upgrade-landmine-modal__left-content--title',
            )}
          >
            {planCopy.title}
          </div>
          <div
            className={getClassNames(
              'universal-upgrade-landmine-modal__left-content--description',
            )}
          >
            to unlock all additional features
          </div>
          <div className="universal-upgrade-landmine-modal__left-content--yearly-pricing">
            <div className="universal-upgrade-landmine-modal__left-content--yearly-pricing--amount">
              ${planPrices.monthlyPricePaidYearly}
            </div>
            /month billed annually
          </div>
          <div
            className={getClassNames(
              'universal-upgrade-landmine-modal__left-content--monthly-pricing',
            )}
          >
            ${planPrices.monthlyPrice}/month billed monthly
          </div>
          {!isEditable && (
            <div className="universal-upgrade-landmine-modal__left-content--cannot-edit">
              Only the account Owner or Admin can upgrade!
            </div>
          )}
          <PrimaryButton
            className={getClassNames(
              'universal-upgrade-landmine-modal__left-content--upgrade-button',
            )}
            css={
              selectedPlan === UpgradeLandmineStates.Startup
                ? {
                    container:
                      '.universal-upgrade-landmine-modal__left-content--upgrade-button--unicorn',
                  }
                : undefined
            }
            size="lg"
            onClick={onUpgrade}
            disabled={!isEditable}
          >
            UPGRADE
          </PrimaryButton>
        </div>
      </div>
      {!november2021Pricing && (
        <div className="universal-upgrade-landmine-modal__right">
          <div
            className={getClassNames(
              'universal-upgrade-landmine-modal__right--recommended',
            )}
          >
            {planCopy.recommendedFor}
          </div>
          <div className="universal-upgrade-landmine-modal__right--features">
            {planCopy.features.map((feature, idx) =>
              !feature.disabled ? (
                <div
                  className="universal-upgrade-landmine-modal__right--feature"
                  key={idx}
                >
                  <CheckCircleIcon
                    style={{ height: 21, width: 21 }}
                    className="universal-upgrade-landmine-modal__right--feature-icon"
                  />
                  {feature.text}
                </div>
              ) : (
                <div
                  className="universal-upgrade-landmine-modal__right--feature"
                  key={idx}
                >
                  <CloseCircleSolidIcon
                    color={colors.red}
                    className="universal-upgrade-landmine-modal__right--feature-icon--disabled"
                  />
                  {feature.text} (
                  <div
                    className="universal-upgrade-landmine-modal__right--feature-link"
                    onClick={() =>
                      feature.availablePlan &&
                      send({
                        type: UpgradeLandmineEvents.switchPlan,
                        plan: feature.availablePlan,
                      })
                    }
                  >
                    {feature.availablePlan}
                  </div>
                  )
                </div>
              ),
            )}
          </div>
          <div className="universal-upgrade-landmine-modal__right--offer">
            {planCopy.callToAction.text}
            <div
              className="universal-upgrade-landmine-modal__right--feature-link"
              onClick={() => window.open(planCopy.callToAction.link)}
            >
              {planCopy.callToAction.linkText}
            </div>
          </div>
        </div>
      )}
    </BasicModalTemplate>
  );
};
